/*
  APS "Find a Retailer" page (sections/aps-find-retailer.liquid).

  - Reads the shops the section printed as JSON (from the Retailer metaobjects).
  - Draws the map with Leaflet + OpenStreetMap tiles, loaded from a CDN with integrity
    hashes. If cdnjs is down it tries unpkg (same file, same hash).
  - Search: one request to OpenStreetMap's place finder (Nominatim) per press of Search.
    Never while typing (their rules forbid that), never more than one a second. The place
    in the visitor's own country (from the browser's language, else the US) is preferred,
    and the other places found are offered as "Not the place you meant?" buttons.
  - "Use my location" asks the browser; nothing is sent anywhere.
  - Nearest shops first, with distance in miles and km. "Show more" adds more.
  - "Browse by country" lists every shop for people who don't search (a country's shops are
    only built when it is opened, so the page stays light).
  - Sells at ("s": store, online, shows; several at once): a small label for each on the card,
    and the "Where they sell" row shows one kind at a time (the lists and the map pins).
  - A shop with no shop front ("s" without store) shows "Based in" its town, region and country,
    never a street or postcode, and has no Directions button. Its pin is its town, so the
    nearest-first list still works.
  - Online with a website: a "Visit website" button. Facebook and Instagram ("fb", "ig"): a
    button each, only for a page on facebook.com, fb.com or instagram.com (https). A Website that
    is one of those pages is shown there instead, never twice. Craft shows & events: a line
    saying to check their Facebook, Instagram or website for dates (only the ones they have).
  - Hours ("hr", one line of text like "By Appointment Only"): "Hours: …" on the card and the map
    pop-up, only when it is filled in (also for a shop with no shop front). Always as text, never HTML.
  - Shops on exactly the same spot (two shops with no shop front in one town share its pin) are
    drawn a little apart on the map, so each pin can be clicked; distances use the real position.
  - If the map or the place finder can't load, the lists still work.
  - No cookies, no tracking, nothing stored.
*/
(function () {
  'use strict';

  var LEAFLET_CSS = [
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css',
    'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
  ];
  var LEAFLET_CSS_SRI = 'sha512-Zcn6bjR/8RZbLEpLIeOwNtzREBAJnUKESxces60Mpoj+2okopSAcSUIUOseddDm0cxnGQzxIR7vJgsLZbdLE3w==';
  var LEAFLET_JS = [
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js',
    'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
  ];
  var LEAFLET_JS_SRI = 'sha512-BwHfrr4c9kmRkLw6iXFdzcdWV/PGkVgiIyIWLLlTSXzWQzxuSg4DiQUCpauz/EWjgk5TYQqX/kvn9pG1NpYfqg==';

  var TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var TILE_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';
  var LEAFLET_PREFIX = '<a href="https://leafletjs.com" target="_blank" rel="noopener">Leaflet</a>';
  var GEOCODER_URL = 'https://nominatim.openstreetmap.org/search';

  var LOAD_TIMEOUT = 12000;
  var GEOCODER_TIMEOUT = 10000;
  var GEOCODER_GAP = 1100;
  var WIDE = '(min-width: 990px)';
  var NARROW = '(max-width: 599px)';
  // Phones and tablets: one finger scrolls the page, two fingers move the map (same test as the CSS hint).
  var TOUCH_ONLY = '(hover: none) and (pointer: coarse)';
  var KM_TO_MI = 0.621371;
  var FALLBACK_COUNTRY = 'us'; // most shops are in the US
  var PREFER_RATIO = 0.75; // a place in the preferred country wins unless the first answer is much better known
  var NEAR_PLACE_KM = 30; // "Not the place you meant?" skips places this close to one already offered
  var MAX_OTHER_PLACES = 4;
  var FIT_MIN_KM = 500; // the map fits the shops within 3 x the nearest one's distance, and at least this far
  var OPEN_ONLY_COUNTRY_UP_TO = 30; // with one country, its list starts open when it has this many shops or fewer
  var SAME_SPOT_DEG = 0.002; // a pin on the same spot as another is drawn this far away (about 200 m) ...
  var SAME_TOWN_DEG = 0.008; // ... a shop with no shop front about 900 m (its pin is only its town anyway), so the
  // pins are apart at the zoom "Show on map" uses (about 30 m a pixel; a pin is 30 pixels wide) ...
  var GOLDEN_ANGLE = 2.39996; // ... each on its own side (a spiral), so none hides another
  // Sells at: the codes in the section's JSON, in the order the labels are shown.
  var SELLS = ['store', 'online', 'shows'];
  // Facebook and Instagram: a page on these sites only (www. allowed before facebook.com, fb.com and instagram.com).
  var SOCIAL_HOSTS = {
    fb: ['facebook.com', 'fb.com', 'm.facebook.com'],
    ig: ['instagram.com']
  };

  var US_STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
    CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
    KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
    MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
    NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico'
  };
  var CA_PROVINCES = {
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut',
    ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon'
  };
  var AU_STATES = {
    ACT: 'Australian Capital Territory', NSW: 'New South Wales', NT: 'Northern Territory',
    QLD: 'Queensland', SA: 'South Australia', TAS: 'Tasmania', VIC: 'Victoria', WA: 'Western Australia'
  };
  // Countries where the list is split by state or province.
  var REGIONS_BY_COUNTRY = {
    'United States': US_STATES,
    Canada: CA_PROVINCES,
    Australia: AU_STATES
  };
  var COUNTRY_ALIASES = {
    us: 'United States', usa: 'United States', 'u.s.': 'United States', 'u.s.a.': 'United States',
    'united states of america': 'United States', america: 'United States',
    uk: 'United Kingdom', 'u.k.': 'United Kingdom', gb: 'United Kingdom', 'great britain': 'United Kingdom',
    britain: 'United Kingdom', england: 'United Kingdom', scotland: 'United Kingdom',
    wales: 'United Kingdom', 'northern ireland': 'United Kingdom'
  };

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ---------- small helpers ---------- */

  function clean(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(/\s+/g, ' ').trim();
  }

  var decoder = document.createElement('textarea');
  function decodeEntities(s) {
    if (s.indexOf('&') === -1) return s;
    decoder.innerHTML = s;
    return decoder.value;
  }

  function text(v) {
    return decodeEntities(clean(v));
  }

  function toNumber(v) {
    if (v === null || v === undefined || v === '') return NaN;
    if (typeof v === 'number') return v;
    return parseFloat(String(v).trim());
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (all, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : all;
    });
  }

  function el(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined && content !== null) node.textContent = content;
    return node;
  }

  function hiddenText(content) {
    return el('span', 'visually-hidden', content);
  }

  function svgIcon(path, evenOdd) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'aps-fr__icon');
    var p = document.createElementNS(ns, 'path');
    p.setAttribute('d', path);
    p.setAttribute('fill', 'currentColor');
    if (evenOdd) p.setAttribute('fill-rule', 'evenodd');
    svg.appendChild(p);
    return svg;
  }

  var ICONS = {
    phone: 'M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z',
    email: 'M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z',
    store: 'M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z',
    shows:
      'M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-1V1h-2zm3 18H5V8h14v11z',
    facebook: 'M14 8h3V4h-3a5 5 0 0 0-5 5v2H7v4h2v8h4v-8h3l1-4h-4V9a1 1 0 0 1 1-1z',
    instagram:
      'M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM17.5 5.2a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6z',
    web: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.9 6h-2.9a15.7 15.7 0 0 0-1.4-3.6A8 8 0 0 1 18.9 8zM12 4c.8 1.2 1.5 2.5 1.9 4h-3.8c.4-1.5 1.1-2.8 1.9-4zM4.3 14a8.2 8.2 0 0 1 0-4h3.4a16.5 16.5 0 0 0 0 4H4.3zm.8 2h2.9c.3 1.3.8 2.5 1.4 3.6A8 8 0 0 1 5.1 16zM8 8H5.1a8 8 0 0 1 4.3-3.6C8.8 5.5 8.4 6.7 8 8zm4 12c-.8-1.2-1.5-2.5-1.9-4h3.8c-.4 1.5-1.1 2.8-1.9 4zm2.3-6H9.7a14.7 14.7 0 0 1 0-4h4.6a14.7 14.7 0 0 1 0 4zm.3 5.6c.6-1.1 1.1-2.3 1.4-3.6h2.9a8 8 0 0 1-4.3 3.6zm1.7-5.6a16.5 16.5 0 0 0 0-4h3.4a8.2 8.2 0 0 1 0 4h-3.4z'
  };

  function haversineKm(aLat, aLng, bLat, bLng) {
    var rad = Math.PI / 180;
    var dLat = (bLat - aLat) * rad;
    var dLng = (bLng - aLng) * rad;
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }

  function pageLocale() {
    return document.documentElement.lang || undefined;
  }

  function formatNumber(n) {
    try {
      if (n < 10) {
        return n.toLocaleString(pageLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      }
      return Math.round(n).toLocaleString(pageLocale());
    } catch (e) {
      return n < 10 ? n.toFixed(1) : String(Math.round(n));
    }
  }

  function safeWebsite(v) {
    var s = clean(v);
    if (!s) return '';
    if (!/^[a-z][a-z0-9+.-]*:/i.test(s)) {
      var bare = s.replace(/^\/+/, '');
      if (!/^[^\s\/]+\.[^\s\/]{2,}/.test(bare)) return '';
      s = 'https://' + bare;
    }
    try {
      var u = new URL(s);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
      return u.href;
    } catch (e) {
      return '';
    }
  }

  // 'fb' or 'ig' when a web address is anywhere on Facebook or Instagram, else ''.
  function socialKind(href) {
    try {
      var host = new URL(href).hostname.toLowerCase();
      for (var kind in SOCIAL_HOSTS) {
        if (!Object.prototype.hasOwnProperty.call(SOCIAL_HOSTS, kind)) continue;
        var hit = SOCIAL_HOSTS[kind].some(function (h) {
          return host === h || host.slice(-h.length - 1) === '.' + h;
        });
        if (hit) return kind;
      }
    } catch (e) {
      return '';
    }
    return '';
  }

  // The https address of a Facebook or Instagram page, or '' for anything else: another site, no page
  // (just the site), a login part or a port. The same rule as retailers.py, so a bad link never shows.
  function safeSocial(v, kind) {
    var href = safeWebsite(v);
    if (!href) return '';
    try {
      var u = new URL(href);
      var host = u.hostname.toLowerCase();
      if (/^www\.[^.]+\.[^.]+$/.test(host)) host = host.slice(4); // never www.m.facebook.com (as retailers.py)
      if (SOCIAL_HOSTS[kind].indexOf(host) === -1) return '';
      if (u.username || u.password || u.port || !u.pathname.replace(/\//g, '')) return '';
      u.protocol = 'https:'; // http becomes https (retailers.py saves them that way too)
      return u.href;
    } catch (e) {
      return '';
    }
  }

  function websiteLabel(href) {
    try {
      var u = new URL(href);
      var host = u.hostname.replace(/^www\./i, '');
      var path = u.pathname.replace(/\/+$/, '');
      if (path && path.length <= 30) return host + path;
      return path ? host + '/…' : host;
    } catch (e) {
      return href;
    }
  }

  function telHref(phone) {
    var first = phone.split(/\s+(?:or|\/)\s+|\s*\/\s*/i)[0];
    first = first.replace(/\s*(?:ext\.?|extension|x|#)\s*\d+\s*$/i, '');
    var digits = first.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
    return digits.replace(/\D/g, '').length >= 6 ? 'tel:' + digits : '';
  }

  function safeEmail(v) {
    var s = clean(v);
    return /^[^\s@<>()",;:?&#\/]+@[^\s@<>()",;:?&#\/]+\.[a-z]{2,}$/i.test(s) ? s : '';
  }

  function normaliseCountry(v) {
    var s = text(v);
    if (!s) return '';
    var alias = COUNTRY_ALIASES[s.toLowerCase()];
    return alias || s;
  }

  // "United States, UK" -> ['united states', 'united kingdom'] (aliases like UK or USA count).
  // Missing setting: United States, United Kingdom. Empty setting: none.
  function firstCountryList(v) {
    var s = v === undefined || v === null ? 'United States, United Kingdom' : String(v);
    var out = [];
    s.split(',').forEach(function (part) {
      var name = normaliseCountry(part).toLowerCase();
      if (name && out.indexOf(name) < 0) out.push(name);
    });
    return out;
  }

  function expandRegion(region, country) {
    if (!region) return '';
    var table = REGIONS_BY_COUNTRY[country];
    if (!table) return region;
    var code = region.replace(/\./g, '').toUpperCase();
    if (table[code]) return table[code];
    var lower = region.toLowerCase();
    for (var key in table) {
      if (Object.prototype.hasOwnProperty.call(table, key) && table[key].toLowerCase() === lower) return table[key];
    }
    return region;
  }

  function sortText(a, b) {
    return a.localeCompare(b, pageLocale(), { sensitivity: 'base' });
  }

  function inViewport(node) {
    var r = node.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    return r.top >= 0 && r.bottom <= h;
  }

  function scrollIntoViewIfNeeded(node, block) {
    if (!inViewport(node)) {
      node.scrollIntoView({ block: block || 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  function readJson(root, selector, fallback) {
    var node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return fallback;
    }
  }

  // The same longitude moved by whole turns of the world so it is within 180 degrees of ref
  // (a map around New Zealand shows a shop at -179 next to one at 179, not across the world).
  function unwrapLng(lng, ref) {
    while (lng - ref < -180) lng += 360;
    while (lng - ref > 180) lng -= 360;
    return lng;
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /* ---------- Leaflet loading (shared by every copy of the section) ---------- */

  var leafletPromise = null;

  function loadOne(make) {
    return new Promise(function (resolve, reject) {
      var node = make();
      var timer = setTimeout(function () {
        cleanup();
        reject(new Error('timeout'));
      }, LOAD_TIMEOUT);
      function cleanup() {
        clearTimeout(timer);
        node.onload = node.onerror = null;
      }
      node.onload = function () {
        cleanup();
        resolve(node);
      };
      node.onerror = function () {
        cleanup();
        if (node.parentNode) node.parentNode.removeChild(node);
        reject(new Error('failed'));
      };
      document.head.appendChild(node);
    });
  }

  function loadFirst(urls, make) {
    var i = 0;
    function next() {
      if (i >= urls.length) return Promise.reject(new Error('all sources failed'));
      var url = urls[i++];
      return loadOne(function () {
        return make(url);
      }).catch(next);
    }
    return next();
  }

  function loadLeaflet() {
    if (window.L && typeof window.L.map === 'function') return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = loadFirst(LEAFLET_CSS, function (url) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.integrity = LEAFLET_CSS_SRI;
      link.crossOrigin = 'anonymous';
      link.referrerPolicy = 'no-referrer';
      return link;
    })
      .then(function () {
        if (window.L && typeof window.L.map === 'function') return null;
        return loadFirst(LEAFLET_JS, function (url) {
          var script = document.createElement('script');
          script.src = url;
          script.integrity = LEAFLET_JS_SRI;
          script.crossOrigin = 'anonymous';
          script.referrerPolicy = 'no-referrer';
          script.async = true;
          return script;
        });
      })
      .then(function () {
        if (!(window.L && typeof window.L.map === 'function')) throw new Error('Leaflet missing');
        return window.L;
      });
    leafletPromise.catch(function () {
      leafletPromise = null;
    });
    return leafletPromise;
  }

  /* ---------- place finder (shared, one request a second at most) ---------- */

  var geocodeCache = {};
  var lastGeocodeAt = 0;

  function preferredCountryCode() {
    var langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''];
    for (var i = 0; i < langs.length; i++) {
      var m = /^[a-z]{2,3}[-_]([a-z]{2})\b/i.exec(langs[i] || '');
      if (m) return m[1].toLowerCase();
    }
    return '';
  }

  function placeLabel(p) {
    var a = p.address || {};
    if (p.addresstype === 'country' || p.type === 'country') return a.country || p.name || p.display_name;
    var town = a.city || a.town || a.village || a.hamlet || a.municipality || a.suburb || a.county || '';
    var parts = [];
    if (a.postcode && /\d/.test(p._query || '')) parts.push(a.postcode);
    [town, a.state || a.province || a.region || '', a.country || ''].forEach(function (part) {
      if (part && parts.indexOf(part) === -1) parts.push(part);
    });
    if (parts.length) return parts.join(', ');
    return clean(p.display_name);
  }

  function countryCode(p) {
    return p && p.address && p.address.country_code ? String(p.address.country_code).toLowerCase() : '';
  }

  // Town names and zip codes repeat between countries ("Birmingham", "33101"), and the place
  // finder's first answer is often abroad. So: the visitor's own country (from the browser's
  // language) if it is among the answers, else the US; unless the first answer is a much better
  // known place (London, England over London, Kentucky). Returns every answer, chosen first.
  function pickPlaces(list, query) {
    var valid = (Array.isArray(list) ? list : []).filter(function (p) {
      return p && isFinite(parseFloat(p.lat)) && isFinite(parseFloat(p.lon));
    });
    if (!valid.length) return [];
    var top = parseFloat(valid[0].importance);
    var chosen = valid[0];
    var prefs = [preferredCountryCode(), FALLBACK_COUNTRY];
    for (var k = 0; k < prefs.length; k++) {
      if (!prefs[k]) continue;
      var hit = null;
      for (var i = 0; i < valid.length; i++) {
        if (countryCode(valid[i]) === prefs[k]) {
          hit = valid[i];
          break;
        }
      }
      if (!hit) continue;
      var importance = parseFloat(hit.importance);
      if (!(top > 0) || !isFinite(importance) || importance >= top * PREFER_RATIO) chosen = hit;
      break;
    }
    var places = [];
    [chosen].concat(valid).forEach(function (p) {
      p._query = query;
      var place = { lat: parseFloat(p.lat), lng: parseFloat(p.lon), label: placeLabel(p) };
      var near = places.some(function (q) {
        return q.label === place.label || haversineKm(q.lat, q.lng, place.lat, place.lng) < NEAR_PLACE_KM;
      });
      if (!near && places.length <= MAX_OTHER_PLACES) places.push(place);
    });
    return places;
  }

  function fetchJson(url, timeout) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer;
    var timedOut = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('timeout'));
      }, timeout);
    });
    var request = fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
    return Promise.race([request, timedOut]).then(
      function (data) {
        clearTimeout(timer);
        return data;
      },
      function (err) {
        clearTimeout(timer);
        throw err;
      }
    );
  }

  // Resolves to the places found (the best one first), or [] when nothing matched. Rejects when
  // the place finder can't be reached.
  function geocode(query) {
    var key = query.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(geocodeCache, key)) return Promise.resolve(geocodeCache[key]);
    var wait = Math.max(0, lastGeocodeAt + GEOCODER_GAP - Date.now());
    return delay(wait)
      .then(function () {
        lastGeocodeAt = Date.now();
        var url =
          GEOCODER_URL + '?format=jsonv2&addressdetails=1&limit=5&q=' + encodeURIComponent(query);
        return fetchJson(url, GEOCODER_TIMEOUT);
      })
      .then(function (list) {
        var places = pickPlaces(list, query);
        geocodeCache[key] = places;
        return places;
      });
  }

  /* ---------- one Find a Retailer section ---------- */

  function FindRetailer(root) {
    this.root = root;
    this.uid = root.id || 'FindRetailer';
    this.config = readJson(root, '[data-aps-fr-config]', {}) || {};
    this.t = this.config.text || {};
    this.perPage = Math.max(1, parseInt(this.config.perPage, 10) || 10);
    this.firstCountries = firstCountryList(this.config.firstCountries);
    this.retailers = this.prepare(readJson(root, '[data-aps-fr-retailers]', []));
    this.byId = {};
    var self = this;
    this.retailers.forEach(function (r) {
      self.byId[r.id] = r;
    });
    this.state = {
      mode: 'none', // 'none' | 'near' | 'match'
      origin: null, // { lat, lng, label, kind: 'place' | 'you' }
      places: [], // every place the last search found (the origin first)
      query: '',
      matches: null,
      category: '',
      sells: '', // '' (all) | 'store' | 'online' | 'shows'
      shown: this.perPage,
      selected: null,
      list: []
    };
    this.busy = false;
    this.openCountries = {};
    this.resultEls = {};
    this.browseEls = {};
    this.browseDetails = {};
    this.browseGroups = {};
    this.countryOf = {};
    this.map = null;
    this.markers = {};
    this.markerKeys = {};

    this.els = {
      app: root.querySelector('[data-app]'),
      form: root.querySelector('[data-search-form]'),
      input: root.querySelector('[data-search-input]'),
      locateRow: root.querySelector('[data-locate-row]'),
      locate: root.querySelector('[data-locate]'),
      filters: root.querySelector('[data-filters]'),
      sellsFilter: root.querySelector('[data-sells-filter]'),
      chips: root.querySelector('[data-chips]'),
      message: root.querySelector('[data-message]'),
      status: root.querySelector('[data-status]'),
      searchButton: root.querySelector('[data-search-button]'),
      mapFrame: root.querySelector('[data-map-frame]'),
      map: root.querySelector('[data-map]'),
      mapMsg: root.querySelector('[data-map-msg]'),
      results: root.querySelector('[data-results]'),
      resultsHeading: root.querySelector('[data-results-heading]'),
      count: root.querySelector('[data-count]'),
      places: root.querySelector('[data-places]'),
      resultsList: root.querySelector('[data-results-list]'),
      more: root.querySelector('[data-more]'),
      browse: root.querySelector('[data-browse]')
    };
    if (!this.els.app) return;

    this.buildFilters();
    this.buildSellsFilter();
    this.bind();
    this.render();
    this.initMap();
  }

  FindRetailer.prototype.prepare = function (raw) {
    var list = [];
    var seen = {};
    var catNames = {};
    (this.config.categories || []).forEach(function (c) {
      catNames[clean(c).toLowerCase()] = clean(c);
    });
    (Array.isArray(raw) ? raw : []).forEach(function (item, i) {
      if (!item || typeof item !== 'object') return;
      var lat = toNumber(item.lat);
      var lng = toNumber(item.lng);
      if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
      if (lat === 0 && lng === 0) return;
      var name = text(item.n);
      if (!name) return;
      var country = normaliseCountry(item.co);
      var region = text(item.r);
      var id = clean(item.h) || 'shop-' + i;
      if (seen[id]) id = id + '-' + i;
      seen[id] = true;
      var cats = [];
      var rawCats = Array.isArray(item.cat) ? item.cat : item.cat ? [item.cat] : [];
      rawCats.forEach(function (c) {
        var label = clean(c);
        if (!label) return;
        var canonical = catNames[label.toLowerCase()] || label;
        if (cats.indexOf(canonical) === -1) cats.push(canonical);
      });
      // Sells at: the codes the section printed (an older page printed "on" for online only).
      var sells = [];
      var rawSells = Array.isArray(item.s) ? item.s : item.on === true || item.on === 'true' ? ['online'] : ['store'];
      rawSells.forEach(function (c) {
        var code = clean(c).toLowerCase();
        if (SELLS.indexOf(code) !== -1 && sells.indexOf(code) === -1) sells.push(code);
      });
      sells.sort(function (a, b) {
        return SELLS.indexOf(a) - SELLS.indexOf(b);
      });
      // No shop front (no Retail store): no street and no postcode, whatever the data holds.
      var noFront = sells.indexOf('store') === -1;
      var website = safeWebsite(item.w);
      var social = { fb: safeSocial(item.fb, 'fb'), ig: safeSocial(item.ig, 'ig') };
      // A Website that is really a Facebook or Instagram page is shown there instead (never twice).
      var kind = website ? socialKind(website) : '';
      if (kind) {
        if (!social[kind]) social[kind] = safeSocial(website, kind);
        website = '';
      }
      var r = {
        id: id,
        index: list.length,
        name: name,
        sells: sells,
        noFront: noFront,
        a1: noFront ? '' : text(item.a1),
        a2: noFront ? '' : text(item.a2),
        city: text(item.c),
        region: region,
        regionFull: expandRegion(region, country),
        zip: noFront ? '' : text(item.z),
        country: country,
        phone: text(item.p),
        email: safeEmail(item.e),
        hours: text(item.hr),
        website: website,
        facebook: social.fb,
        instagram: social.ig,
        lat: lat,
        lng: lng,
        cats: cats,
        km: null
      };
      r.haystack = [r.name, r.city, r.region, r.regionFull, r.zip, r.country, r.a1]
        .join(' ')
        .toLowerCase();
      list.push(r);
    });
    // Where each pin is drawn: its own position, except that a shop on exactly the same spot as one before it (two
    // shops with no shop front in one town share the town's pin) is drawn a little apart, or one pin would hide the other.
    // Distances, the nearest-first list and Directions keep using r.lat and r.lng.
    var atSpot = {};
    list.forEach(function (r) {
      var key = r.lat.toFixed(5) + ',' + r.lng.toFixed(5);
      var k = atSpot[key] || 0;
      atSpot[key] = k + 1;
      r.pinLat = r.lat;
      r.pinLng = r.lng;
      if (k) {
        var angle = k * GOLDEN_ANGLE;
        var away = (r.noFront ? SAME_TOWN_DEG : SAME_SPOT_DEG) * Math.sqrt(k);
        r.pinLat = Math.max(-85, Math.min(85, r.lat + away * Math.cos(angle)));
        r.pinLng = r.lng + (away * Math.sin(angle)) / Math.max(0.2, Math.cos((r.lat * Math.PI) / 180));
      }
    });
    return list;
  };

  /* ----- events ----- */

  FindRetailer.prototype.bind = function () {
    var self = this;
    var els = this.els;

    els.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.search();
    });

    if (navigator.geolocation && window.isSecureContext !== false) {
      els.locate.addEventListener('click', function () {
        self.locate();
      });
    } else {
      els.locateRow.hidden = true;
    }

    els.more.addEventListener('click', function () {
      self.showMore();
    });

    els.app.addEventListener('click', function (e) {
      var target = e.target;
      var mapButton = target.closest('[data-show-on-map]');
      if (mapButton) {
        self.showOnMap(mapButton.getAttribute('data-show-on-map'), true);
        return;
      }
      if (target.closest('a, button, input, label, summary, select, textarea')) return;
      var card = target.closest('.aps-fr__card');
      if (!card || !self.map) return;
      var sel = window.getSelection ? String(window.getSelection()) : '';
      if (sel) return;
      self.showOnMap(card.getAttribute('data-id'), false);
    });

    // A country's shops are built when it is first opened (the click comes just before it opens).
    els.browse.addEventListener('click', function (e) {
      var summary = e.target.closest('summary');
      var d = summary && summary.parentNode;
      if (d && d.matches && d.matches('details[data-country]')) self.fillCountry(d.getAttribute('data-country'));
    });
    els.browse.addEventListener(
      'toggle',
      function (e) {
        var d = e.target;
        if (d && d.matches && d.matches('details[data-country]')) {
          var country = d.getAttribute('data-country');
          self.openCountries[country] = d.open;
          if (d.open) self.fillCountry(country);
        }
      },
      true
    );

    if (els.places) {
      els.places.addEventListener('click', function (e) {
        var b = e.target.closest('[data-place]');
        if (!b) return;
        var i = parseInt(b.getAttribute('data-place'), 10);
        var places = self.state.places.slice();
        var place = places.splice(i, 1)[0];
        if (!place) return;
        places.unshift(place);
        self.setOrigin({ lat: place.lat, lng: place.lng, label: place.label, kind: 'place' }, places);
      });
    }
  };

  // Read out by screen readers only.
  FindRetailer.prototype.setStatus = function (message) {
    var status = this.els.status;
    clearTimeout(this.statusTimer);
    status.textContent = '';
    if (!message) return;
    // A short pause makes screen readers announce the same message twice in a row.
    this.statusTimer = setTimeout(function () {
      status.textContent = message;
    }, 60);
  };

  // Shown on the page (problems and tips) and read out.
  FindRetailer.prototype.setMessage = function (message) {
    this.els.message.textContent = message || '';
    this.els.message.hidden = !message;
    this.setStatus(message);
  };

  // While searching, the pressed button says what's happening ("Searching…").
  FindRetailer.prototype.setBusy = function (button, label) {
    var busy = !!button;
    this.busy = busy;
    this.els.form.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.root.classList.toggle('is-busy', busy);
    if (busy) {
      this.busyButton = button;
      this.busyLabel = button.innerHTML;
      var span = button.querySelector('span');
      (span || button).textContent = label;
      button.setAttribute('aria-busy', 'true');
    } else if (this.busyButton) {
      this.busyButton.innerHTML = this.busyLabel;
      this.busyButton.removeAttribute('aria-busy');
      this.busyButton = null;
    }
  };

  FindRetailer.prototype.search = function () {
    var self = this;
    var query = clean(this.els.input.value);
    if (!query) {
      this.setMessage(this.t.emptyQuery);
      this.els.input.focus();
      return;
    }
    if (this.busy) return;
    this.setBusy(this.els.searchButton, this.t.searchingButton);
    this.setStatus(fill(this.t.searching, { query: query }));
    geocode(query)
      .then(
        function (places) {
          self.setBusy(null);
          var place = places[0];
          if (!place) {
            self.setMessage(fill(self.t.notFound, { query: query }));
            return;
          }
          self.setOrigin({ lat: place.lat, lng: place.lng, label: place.label, kind: 'place' }, places);
        },
        function () {
          self.setBusy(null);
          self.textMatch(query);
        }
      );
  };

  FindRetailer.prototype.locate = function () {
    var self = this;
    if (this.busy) return;
    this.setBusy(this.els.locate, this.t.locatingButton);
    this.setStatus(this.t.locating);
    var finished = false;
    var safety = setTimeout(function () {
      if (finished) return;
      finished = true;
      self.setBusy(null);
      self.setMessage(self.t.locateFailed);
    }, 30000);
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        if (finished) return;
        finished = true;
        clearTimeout(safety);
        self.setBusy(null);
        self.setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: '', kind: 'you' });
      },
      function (err) {
        if (finished) return;
        finished = true;
        clearTimeout(safety);
        self.setBusy(null);
        self.setMessage(err && err.code === 1 ? self.t.locateDenied : self.t.locateFailed);
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 }
    );
  };

  FindRetailer.prototype.setOrigin = function (origin, places) {
    var self = this;
    this.setMessage('');
    this.state.mode = 'near';
    this.state.origin = origin;
    this.state.places = places || [];
    this.state.query = '';
    this.state.matches = null;
    this.state.shown = this.perPage;
    this.retailers.forEach(function (r) {
      r.km = haversineKm(origin.lat, origin.lng, r.lat, r.lng);
    });
    this.clearSelection();
    this.render();
    this.updateOriginMarker();
    this.fitToResults();
    this.afterResults();
    var first = this.state.list[0];
    if (first) {
      this.setStatus(
        fill(this.t.announceNear, {
          shown: Math.min(this.state.shown, this.state.list.length),
          name: first.name,
          distance: this.distanceText(first.km)
        })
      );
    } else {
      this.setStatus(this.els.count.textContent);
    }
  };

  FindRetailer.prototype.textMatch = function (query) {
    var tokens = query.toLowerCase().split(/[\s,]+/).filter(Boolean);
    var matches = {};
    var any = false;
    this.retailers.forEach(function (r) {
      var ok = tokens.every(function (tok) {
        return r.haystack.indexOf(tok) !== -1;
      });
      if (ok) {
        matches[r.id] = true;
        any = true;
      }
    });
    if (!any) {
      this.setMessage(fill(this.t.geocoderDownNone, { query: query }));
      return;
    }
    this.state.mode = 'match';
    this.state.origin = null;
    this.state.places = [];
    this.state.query = query;
    this.state.matches = matches;
    this.state.shown = this.perPage;
    this.retailers.forEach(function (r) {
      r.km = null;
    });
    this.clearSelection();
    this.render();
    this.updateOriginMarker();
    this.fitToResults();
    this.afterResults();
    this.setMessage(fill(this.t.geocoderDown, { query: query }));
  };

  FindRetailer.prototype.afterResults = function () {
    var heading = this.els.resultsHeading;
    try {
      heading.focus({ preventScroll: true });
    } catch (e) {
      heading.focus();
    }
    // Phones: bring the map (with the list under it) into view. Computers: the list's heading.
    var target = window.matchMedia(WIDE).matches ? this.els.resultsHeading : this.els.mapFrame;
    if (this.root.classList.contains('aps-fr--no-map')) target = this.els.resultsHeading;
    scrollIntoViewIfNeeded(target, 'start');
  };

  FindRetailer.prototype.showMore = function () {
    var before = Math.min(this.state.shown, this.state.list.length);
    this.state.shown += this.perPage;
    this.renderResults();
    this.renderMarkers();
    this.refreshSelection();
    var firstNew = this.state.list[before];
    if (firstNew && this.resultEls[firstNew.id]) {
      var h = this.resultEls[firstNew.id].querySelector('.aps-fr__name');
      if (h) h.focus();
    }
    this.setStatus(this.els.count.textContent);
  };

  FindRetailer.prototype.setCategory = function (category) {
    this.state.category = category;
    this.afterFilter();
  };

  // "Where they sell": '' (all), 'store', 'online' or 'shows'.
  FindRetailer.prototype.setSells = function (code) {
    this.state.sells = code;
    if (this.els.sellsFilter) {
      Array.prototype.forEach.call(this.els.sellsFilter.querySelectorAll('[data-sells]'), function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-sells') === code ? 'true' : 'false');
      });
    }
    this.afterFilter();
  };

  FindRetailer.prototype.afterFilter = function () {
    var s = this.state;
    s.shown = this.perPage;
    this.clearSelection();
    this.render();
    if (s.mode !== 'none') {
      this.fitToResults();
      this.setStatus(this.els.count.textContent);
      return;
    }
    var t = this.t;
    var values = { total: this.filtered().length, category: s.category, what: this.sellsWhat() };
    if (s.sells) {
      this.setStatus(fill(s.category ? t.filterSellsCategory : t.filterSells, values));
    } else {
      this.setStatus(fill(s.category ? t.filterCount : t.filterAll, values));
    }
  };

  // The chosen kind of shop in words ("online shops"), or '' for all.
  FindRetailer.prototype.sellsWhat = function () {
    var t = this.t;
    var words = { store: t.sellsWhatStore, online: t.sellsWhatOnline, shows: t.sellsWhatShows };
    return this.state.sells ? words[this.state.sells] || '' : '';
  };

  // What to say when the filters leave no shop. In the results after a text search (inResults), "All" only
  // brings back the shops that match it, so the words say that, not "every shop".
  FindRetailer.prototype.noneText = function (inResults) {
    var s = this.state;
    var t = this.t;
    var values = { category: s.category, what: this.sellsWhat(), query: s.query };
    if (inResults && s.mode === 'match') {
      if (s.sells) return fill(s.category ? t.noneMatchSellsCategory : t.noneMatchSells, values);
      return fill(t.noneMatchCategory, values);
    }
    if (s.sells) return fill(s.category ? t.noneSellsCategory : t.noneSells, values);
    return fill(t.noneCategory, values);
  };

  /* ----- lists ----- */

  FindRetailer.prototype.filtered = function () {
    var cat = this.state.category;
    var sells = this.state.sells;
    if (!cat && !sells) return this.retailers.slice();
    return this.retailers.filter(function (r) {
      return (!cat || r.cats.indexOf(cat) !== -1) && (!sells || r.sells.indexOf(sells) !== -1);
    });
  };

  FindRetailer.prototype.currentList = function () {
    var s = this.state;
    var list = this.filtered();
    if (s.mode === 'near') {
      return list.sort(function (a, b) {
        return a.km - b.km || sortText(a.name, b.name);
      });
    }
    if (s.mode === 'match') {
      return list
        .filter(function (r) {
          return s.matches && s.matches[r.id];
        })
        .sort(function (a, b) {
          return sortText(a.name, b.name);
        });
    }
    return [];
  };

  FindRetailer.prototype.render = function () {
    this.state.list = this.currentList();
    this.renderResults();
    this.renderBrowse();
    this.renderMarkers();
    this.refreshSelection();
  };

  FindRetailer.prototype.distanceText = function (km) {
    if (km === null || km === undefined || !isFinite(km)) return '';
    return fill(this.t.distance, { mi: formatNumber(km * KM_TO_MI), km: formatNumber(km) });
  };

  // "Hours: By Appointment Only" (the text goes in as it is: el() sets it as text, never HTML).
  FindRetailer.prototype.hoursText = function (r) {
    return fill(this.t.hours || 'Hours: [hours]', { hours: r.hours });
  };

  // Hours one day range a line: "Mon-Thursday : 9am to 6pm | Friday-Saturday: 9am-7pm | Sun: 10am to 6pm" ->
  // "Mon–Thu: 9am–6pm", "Fri–Sat: 9am–7pm", "Sun: 10am–6pm". A | or ; starts a new line; so does a day name (or a
  // range of them) followed by a colon, a time or "closed". Text with no day name in it stays as it is.
  var DAY_NAMES = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
  var DAY = '\\b(?:mon(?:day)?|tue(?:s(?:day)?)?|wed(?:s|nesday)?|thu(?:r(?:s(?:day)?)?)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)(?![a-z])\\.?';
  var DAYS = DAY + '(?:\\s*(?:-|–|—|to|thru|through|&|and|,)\\s*' + DAY + ')*';
  var DAY_START = new RegExp(DAYS + '(?=\\s*(?::|\\d|closed\\b))', 'gi');
  var TIME = '\\d{1,2}(?:[:.]\\d{2})?\\s*(?:[ap]\\.?m\\.?)?';
  function hoursLines(hours) {
    var lines = [];
    String(hours || '').split(/\s*[|;]\s*/).forEach(function (part) {
      var starts = [], m;
      DAY_START.lastIndex = 0;
      while ((m = DAY_START.exec(part))) { if (m.index) starts.push(m.index); }
      var from = 0;
      starts.concat([part.length]).forEach(function (to) { lines.push(part.slice(from, to)); from = to; });
    });
    return lines.map(function (line) {
      if (!new RegExp(DAY, 'i').test(line)) return line.trim();
      return line
        .replace(new RegExp(DAY, 'gi'), function (d) { return DAY_NAMES[d.slice(0, 3).toLowerCase()]; })
        .replace(/(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s*(?:-|–|—|to|thru|through)\s*(?=Mon|Tue|Wed|Thu|Fri|Sat|Sun)/g, '$1–')
        .replace(new RegExp('(' + TIME + ')\\s*(?:-|–|—|to)\\s*(?=\\d)', 'gi'), function (all, t) { return t.trim() + '–'; })
        .replace(/^((?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[^:\d]*?)\s*:?\s*(?=\d|closed)/i, '$1: ')
        .replace(/: closed\b/i, ': Closed').replace(/\s+/g, ' ').replace(/^[\s,]+|[\s,]+$/g, '');
    }).filter(Boolean);
  }

  // The Hours on a card or in the pop-up: one line as before, or "Hours:" and then a line for each day range.
  FindRetailer.prototype.hoursNode = function (r, tag, className) {
    var lines = hoursLines(r.hours);
    if (lines.length < 2) return el(tag, className, this.hoursText({ hours: lines[0] || r.hours }));
    var node = el(tag, className + ' is-days');
    node.appendChild(document.createTextNode(fill(this.t.hours || 'Hours: [hours]', { hours: '' }).trim()));
    lines.forEach(function (line) {
      node.appendChild(document.createElement('br'));
      node.appendChild(document.createTextNode(line));
    });
    return node;
  };

  // A shop with no shop front: "Based in Town, Region, Country" (never a street or postcode).
  FindRetailer.prototype.basedInText = function (r) {
    var parts = [];
    var region = r.regionFull || r.region;
    [r.city, region, r.country].forEach(function (part) {
      if (!part) return;
      var twice = parts.some(function (p) {
        return p.toLowerCase() === part.toLowerCase();
      });
      if (!twice) parts.push(part);
    });
    return parts.length ? fill(this.t.basedIn || 'Based in [place]', { place: parts.join(', ') }) : '';
  };

  // Sells at in words: "Retail store", "Online", "Craft shows & events".
  FindRetailer.prototype.sellsNames = function (r) {
    var t = this.t;
    var words = { store: t.sellsStore, online: t.sellsOnline, shows: t.sellsShows };
    return r.sells.map(function (code) {
      return words[code] || code;
    });
  };

  // A small label for each Sells at choice, under the shop's name.
  FindRetailer.prototype.sellsLabels = function (r) {
    var names = this.sellsNames(r);
    var list = el('ul', 'aps-fr__sells');
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', this.t.sellsAt);
    var icons = { store: ICONS.store, online: ICONS.web, shows: ICONS.shows };
    r.sells.forEach(function (code, i) {
      var item = el('li', 'aps-fr__sell aps-fr__sell--' + code);
      item.appendChild(svgIcon(icons[code]));
      item.appendChild(el('span', null, names[i]));
      list.appendChild(item);
    });
    return list;
  };

  // Craft shows & events: "Find them at craft shows & events – check their Facebook, Instagram or website for
  // dates", naming only the links the shop has.
  FindRetailer.prototype.showsText = function (r) {
    var t = this.t;
    var links = [];
    if (r.facebook) links.push(t.facebook);
    if (r.instagram) links.push(t.instagram);
    if (r.website) links.push(t.linkWebsite);
    if (!links.length) return t.showsLineNoLinks;
    var last = links.pop();
    var named = links.length ? links.join(', ') + ' ' + t.or + ' ' + last : last;
    return fill(t.showsLine, { links: named });
  };

  // A link that opens in a new tab, styled as a button; the shop's name is read out with it.
  FindRetailer.prototype.newTabLink = function (className, href, label, name) {
    var a = el('a', className);
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener';
    a.appendChild(document.createTextNode(label));
    a.appendChild(hiddenText(' – ' + name + ' ' + this.t.newTab));
    return a;
  };

  FindRetailer.prototype.buildCard = function (r, opts) {
    var t = this.t;
    var li = el('li', 'aps-fr__item');
    var card = el('article', 'aps-fr__card');
    card.setAttribute('data-id', r.id);
    var headingId = this.uid + '-' + opts.context + '-' + r.index;
    card.setAttribute('aria-labelledby', headingId);

    var head = el('div', 'aps-fr__card-head');
    if (opts.num) {
      var num = el('span', 'aps-fr__num', String(opts.num));
      num.setAttribute('aria-hidden', 'true');
      head.appendChild(num);
    }
    var name = el('h' + opts.level, 'aps-fr__name', r.name);
    name.id = headingId;
    name.tabIndex = -1;
    head.appendChild(name);
    card.appendChild(head);

    if (r.sells.length) card.appendChild(this.sellsLabels(r));

    if (r.km !== null && this.state.origin) {
      card.appendChild(el('p', 'aps-fr__distance', this.distanceText(r.km)));
    }

    var lines = [];
    if (r.noFront) {
      var based = this.basedInText(r);
      if (based) lines.push(based);
    } else {
      if (r.a1) lines.push(r.a1);
      if (r.a2) lines.push(r.a2);
      var place = r.city;
      if (r.region && r.region.toLowerCase() !== r.city.toLowerCase()) {
        place = place ? place + ', ' + r.region : r.region;
      }
      if (r.zip) place = place ? place + ' ' + r.zip : r.zip;
      if (place) lines.push(place);
      if (r.country) lines.push(r.country);
    }
    if (lines.length) {
      var address = el('p', 'aps-fr__address');
      lines.forEach(function (line, i) {
        if (i) address.appendChild(document.createElement('br'));
        address.appendChild(document.createTextNode(line));
      });
      card.appendChild(address);
    }

    if (r.hours) card.appendChild(this.hoursNode(r, 'p', 'aps-fr__hours'));

    if (r.sells.indexOf('shows') !== -1) card.appendChild(el('p', 'aps-fr__shows', this.showsText(r)));

    var contact = el('ul', 'aps-fr__contact');
    contact.setAttribute('role', 'list');
    var tel = r.phone ? telHref(r.phone) : '';
    if (r.phone) {
      var phoneItem = el('li');
      var phoneNode = tel ? el('a', 'aps-fr__contact-link') : el('span', 'aps-fr__contact-text');
      if (tel) phoneNode.href = tel;
      phoneNode.appendChild(svgIcon(ICONS.phone));
      phoneNode.appendChild(hiddenText(t.phone + ': '));
      phoneNode.appendChild(el('span', null, r.phone));
      phoneItem.appendChild(phoneNode);
      contact.appendChild(phoneItem);
    }
    if (r.email) {
      var emailItem = el('li');
      var mail = el('a', 'aps-fr__contact-link');
      mail.href = 'mailto:' + r.email;
      mail.appendChild(svgIcon(ICONS.email));
      mail.appendChild(hiddenText(t.email + ': '));
      mail.appendChild(el('span', 'aps-fr__break', r.email));
      emailItem.appendChild(mail);
      contact.appendChild(emailItem);
    }
    if (r.website) {
      var webItem = el('li');
      var web = el('a', 'aps-fr__contact-link');
      web.href = r.website;
      web.target = '_blank';
      web.rel = 'noopener';
      web.appendChild(svgIcon(ICONS.web));
      web.appendChild(hiddenText(t.website + ': '));
      web.appendChild(el('span', 'aps-fr__break', websiteLabel(r.website)));
      web.appendChild(hiddenText(' ' + t.newTab));
      webItem.appendChild(web);
      contact.appendChild(webItem);
    }
    if (contact.children.length) card.appendChild(contact);

    // Facebook and Instagram: a button each (only a page on their own sites; see safeSocial).
    var social = el('ul', 'aps-fr__social');
    social.setAttribute('role', 'list');
    var self = this;
    [
      { href: r.facebook, label: t.facebook, icon: ICONS.facebook },
      { href: r.instagram, label: t.instagram, icon: ICONS.instagram, evenOdd: true }
    ].forEach(function (s) {
      if (!s.href) return;
      var item = el('li');
      var link = self.newTabLink('aps-fr__social-link', s.href, s.label, r.name);
      link.insertBefore(svgIcon(s.icon, s.evenOdd), link.firstChild);
      item.appendChild(link);
      social.appendChild(item);
    });
    if (social.children.length) card.appendChild(social);

    if (r.cats.length) {
      var badges = el('ul', 'aps-fr__badges');
      badges.setAttribute('role', 'list');
      badges.setAttribute('aria-label', t.carries);
      r.cats.forEach(function (c) {
        badges.appendChild(el('li', 'aps-fr__badge', c));
      });
      card.appendChild(badges);
    }

    var actions = el('div', 'aps-fr__actions');
    // No shop front, so no Directions (there is no shop to visit).
    if (!r.noFront) {
      var directions =
        'https://www.google.com/maps/dir/?api=1&destination=' + r.lat.toFixed(6) + ',' + r.lng.toFixed(6);
      actions.appendChild(this.newTabLink('button aps-fr__action', directions, t.directions, r.name));
    }
    // Sells online: the website is the way to buy.
    if (r.website && r.sells.indexOf('online') !== -1) {
      actions.appendChild(
        this.newTabLink('button aps-fr__action aps-fr__action--website', r.website, t.visitWebsite, r.name)
      );
    }
    var onMap = el('button', 'button button--secondary aps-fr__action aps-fr__action--map');
    onMap.type = 'button';
    onMap.setAttribute('data-show-on-map', r.id);
    onMap.appendChild(document.createTextNode(t.showOnMap));
    onMap.appendChild(hiddenText(' – ' + r.name));
    actions.appendChild(onMap);
    card.appendChild(actions);

    li.appendChild(card);
    return li;
  };

  FindRetailer.prototype.renderResults = function () {
    var s = this.state;
    var els = this.els;
    var t = this.t;
    var self = this;
    this.resultEls = {};
    if (s.mode === 'none') {
      els.results.hidden = true;
      els.resultsList.textContent = '';
      this.renderPlaces();
      return;
    }
    els.results.hidden = false;
    if (s.mode === 'match') {
      els.resultsHeading.textContent = fill(t.headingMatch, { query: s.query });
    } else if (s.origin && s.origin.kind === 'you') {
      els.resultsHeading.textContent = t.headingYou;
    } else {
      els.resultsHeading.textContent = fill(t.headingPlace, { place: s.origin ? s.origin.label : '' });
    }
    var total = s.list.length;
    var shown = Math.min(s.shown, total);
    if (!total && (s.category || s.sells)) {
      els.count.textContent = this.noneText(true);
    } else {
      var count = s.sells ? (s.category ? t.countSellsCategory : t.countSells) : s.category ? t.countCategory : t.count;
      els.count.textContent = fill(count, { shown: shown, total: total, category: s.category, what: this.sellsWhat() });
    }
    this.renderPlaces();
    var frag = document.createDocumentFragment();
    s.list.slice(0, shown).forEach(function (r, i) {
      var li = self.buildCard(r, { num: i + 1, level: 3, context: 'result' });
      self.resultEls[r.id] = li.firstChild;
      frag.appendChild(li);
    });
    els.resultsList.textContent = '';
    els.resultsList.appendChild(frag);
    els.more.hidden = shown >= total;
    els.more.textContent = t.showMore;
  };

  // "Not the place you meant?": the other places the search found, one button each (no new request).
  FindRetailer.prototype.renderPlaces = function () {
    var box = this.els.places;
    if (!box) return;
    var s = this.state;
    var t = this.t;
    box.textContent = '';
    var others = s.mode === 'near' && s.origin && s.origin.kind === 'place' ? s.places.slice(1) : [];
    box.hidden = !others.length;
    if (!others.length) return;
    box.appendChild(el('p', 'aps-fr__places-label', t.otherPlaces));
    var ul = el('ul', 'aps-fr__places-list');
    ul.setAttribute('role', 'list');
    others.forEach(function (p, i) {
      var li = el('li');
      var b = el('button', 'aps-fr__place');
      b.type = 'button';
      b.setAttribute('data-place', String(i + 1));
      b.appendChild(hiddenText(t.otherPlaceButton + ' '));
      b.appendChild(document.createTextNode(p.label));
      li.appendChild(b);
      ul.appendChild(li);
    });
    box.appendChild(ul);
  };

  // The country list: only each country's line is built here. Its shops are built the first time
  // it is opened (fillCountry), and again on a search or filter only while it is open.
  FindRetailer.prototype.renderBrowse = function () {
    var self = this;
    var t = this.t;
    var list = this.filtered();
    this.browseEls = {};
    this.browseDetails = {};
    this.browseGroups = {};
    this.countryOf = {};
    var byCountry = {};
    list.forEach(function (r) {
      var c = r.country || t.otherRegion;
      (byCountry[c] = byCountry[c] || []).push(r);
      self.countryOf[r.id] = c;
    });
    // "Countries listed first" in their order, then the rest by number of shops (most first),
    // then A to Z; shops with no country ("Other") always last.
    var first = this.firstCountries;
    var rank = function (c) {
      if (c === t.otherRegion) return first.length + 1;
      var i = first.indexOf(c.toLowerCase());
      return i < 0 ? first.length : i;
    };
    var countries = Object.keys(byCountry).sort(function (a, b) {
      return rank(a) - rank(b) || byCountry[b].length - byCountry[a].length || sortText(a, b);
    });
    var onlyOne = countries.length === 1 && byCountry[countries[0]].length <= OPEN_ONLY_COUNTRY_UP_TO;
    var frag = document.createDocumentFragment();

    if (!countries.length) {
      frag.appendChild(el('p', 'aps-fr__empty', this.noneText()));
    }

    countries.forEach(function (country) {
      var shops = byCountry[country];
      var details = el('details', 'aps-fr__country');
      details.setAttribute('data-country', country);
      var summary = el('summary', 'aps-fr__country-summary');
      summary.appendChild(el('span', 'aps-fr__country-name', country));
      summary.appendChild(
        el('span', 'aps-fr__country-count', shops.length === 1 ? t.shopOne : fill(t.shopOther, { count: shops.length }))
      );
      details.appendChild(summary);
      self.browseGroups[country] = shops;
      self.browseDetails[country] = details;
      if (onlyOne || self.openCountries[country]) {
        details.open = true;
        self.fillCountry(country);
      }
      frag.appendChild(details);
    });
    this.els.browse.textContent = '';
    this.els.browse.appendChild(frag);
  };

  // Builds one country's shops (once). Returns its <details>.
  FindRetailer.prototype.fillCountry = function (country) {
    var self = this;
    var t = this.t;
    var details = this.browseDetails[country];
    var shops = this.browseGroups[country];
    if (!details || !shops || details.hasAttribute('data-filled')) return details || null;
    details.setAttribute('data-filled', '');
    var body = el('div', 'aps-fr__country-body');
    var added = [];

    var bySortedPlace = function (a, b) {
      return sortText(a.city || '~', b.city || '~') || sortText(a.name, b.name);
    };
    var addCards = function (group, level) {
      var ul = el('ul', 'aps-fr__cards aps-fr__cards--browse');
      ul.setAttribute('role', 'list');
      group.sort(bySortedPlace).forEach(function (r) {
        var li = self.buildCard(r, { level: level, context: 'browse' });
        self.browseEls[r.id] = li.firstChild;
        added.push(r.id);
        ul.appendChild(li);
      });
      body.appendChild(ul);
    };

    if (REGIONS_BY_COUNTRY[country]) {
      var byRegion = {};
      shops.forEach(function (r) {
        var key = r.regionFull || t.otherRegion;
        (byRegion[key] = byRegion[key] || []).push(r);
      });
      var regions = Object.keys(byRegion).sort(function (a, b) {
        if (a === t.otherRegion) return 1;
        if (b === t.otherRegion) return -1;
        return sortText(a, b);
      });
      regions.forEach(function (region) {
        body.appendChild(el('h3', 'aps-fr__region', region));
        addCards(byRegion[region], 4);
      });
    } else {
      addCards(shops.slice(), 3);
    }
    details.appendChild(body);
    var selected = this.state.selected;
    if (selected && added.indexOf(selected) !== -1) this.browseEls[selected].classList.add('is-selected');
    return details;
  };

  // The section prints a button for each of the four categories that a shop carries.
  // Any other category typed into a shop gets a button added here.
  FindRetailer.prototype.buildFilters = function () {
    var self = this;
    var chips = this.els.chips;
    var name = this.uid + '-category';
    var have = {};
    Array.prototype.forEach.call(chips.querySelectorAll('input[type=radio]'), function (input) {
      have[input.value] = true;
      name = input.name;
    });
    var extra = {};
    this.retailers.forEach(function (r) {
      r.cats.forEach(function (c) {
        if (!have[c]) extra[c] = true;
      });
    });
    var add = Object.keys(extra).sort(sortText);
    if (add.length && !have['']) add.unshift('');
    add.forEach(function (c) {
      var label = el('label', 'aps-fr__chip');
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = c;
      input.className = 'aps-fr__chip-input';
      if (!c) input.checked = true;
      label.appendChild(input);
      label.appendChild(el('span', 'aps-fr__chip-text', c || self.t.allShops));
      chips.appendChild(label);
    });
    var inputs = chips.querySelectorAll('input[type=radio]');
    if (!inputs.length) return;
    Array.prototype.forEach.call(inputs, function (input) {
      input.addEventListener('change', function () {
        if (input.checked) self.setCategory(input.value);
      });
    });
    // A browser may bring back a choice from before a page reload.
    var checked = chips.querySelector('input[type=radio]:checked');
    if (checked && checked.value) this.state.category = checked.value;
    this.els.filters.hidden = false;
  };

  // "Where they sell" (All, Retail stores, Online, Craft shows & events): shown when it would change what is shown,
  // with a button only for the kinds some shop is. One choice at a time (aria-pressed says which).
  FindRetailer.prototype.buildSellsFilter = function () {
    var self = this;
    var row = this.els.sellsFilter;
    if (!row) return;
    var counts = {};
    this.retailers.forEach(function (r) {
      r.sells.forEach(function (code) {
        counts[code] = (counts[code] || 0) + 1;
      });
    });
    var total = this.retailers.length;
    var useful = SELLS.some(function (code) {
      return counts[code] > 0 && counts[code] < total;
    });
    var buttons = row.querySelectorAll('[data-sells]');
    Array.prototype.forEach.call(buttons, function (b) {
      var code = b.getAttribute('data-sells');
      b.hidden = !!code && !counts[code];
      b.addEventListener('click', function () {
        if (self.state.sells !== code) self.setSells(code);
      });
    });
    row.hidden = !useful;
  };

  /* ----- selection (list <-> map) ----- */

  FindRetailer.prototype.clearSelection = function () {
    this.state.selected = null;
    if (this.map && this.popup && this.map.hasLayer(this.popup)) {
      this.ignorePopupClose = true;
      this.map.closePopup(this.popup);
      this.ignorePopupClose = false;
    }
  };

  FindRetailer.prototype.refreshSelection = function () {
    var id = this.state.selected;
    var groups = [this.resultEls, this.browseEls];
    groups.forEach(function (group) {
      Object.keys(group).forEach(function (key) {
        group[key].classList.toggle('is-selected', key === id);
      });
    });
    this.renderMarkers();
  };

  FindRetailer.prototype.select = function (id) {
    if (!this.byId[id]) return;
    this.state.selected = id;
    this.refreshSelection();
  };

  FindRetailer.prototype.showOnMap = function (id, scrollToMap) {
    var r = this.byId[id];
    if (!r) return;
    this.select(id);
    if (!this.map) return;
    var self = this;
    var map = this.map;
    var zoom = Math.max(map.getZoom(), 12);
    // Put the pin a little below the middle so its pop-up fits above it on small maps.
    var lift = Math.min(110, map.getSize().y / 2 - 60);
    var at = this.markers[id] ? this.markers[id].getLatLng() : [r.pinLat, r.pinLng]; // the pin's copy of the world
    var center = map.unproject(map.project(at, zoom).subtract([0, Math.max(0, lift)]), zoom);
    var opened = false;
    var open = function () {
      if (opened) return;
      opened = true;
      map.off('moveend', open);
      if (self.state.selected === id) self.openPopup(r);
    };
    map.on('moveend', open);
    map.setView(center, zoom, { animate: !reduceMotion });
    setTimeout(open, 900);
    if (scrollToMap && !inViewport(this.els.mapFrame)) {
      this.els.mapFrame.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  };

  // Finds the shop's card (making it visible first) and returns it.
  FindRetailer.prototype.cardFor = function (id) {
    var s = this.state;
    var index = -1;
    for (var i = 0; i < s.list.length; i++) {
      if (s.list[i].id === id) {
        index = i;
        break;
      }
    }
    if (index !== -1) {
      if (index >= s.shown) {
        s.shown = Math.ceil((index + 1) / this.perPage) * this.perPage;
        this.renderResults();
        this.renderMarkers();
        this.refreshSelection();
      }
      if (this.resultEls[id]) return this.resultEls[id];
    }
    var country = this.countryOf[id];
    var details = country ? this.fillCountry(country) : null;
    if (details && !details.open) {
      details.open = true;
      this.openCountries[country] = true;
    }
    return this.browseEls[id] || null;
  };

  FindRetailer.prototype.revealCard = function (id, moveFocus) {
    var card = this.cardFor(id);
    if (!card) return;
    if (moveFocus) {
      var name = card.querySelector('.aps-fr__name');
      card.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      if (name) {
        try {
          name.focus({ preventScroll: true });
        } catch (e) {
          name.focus();
        }
      }
    } else if (window.matchMedia(WIDE).matches) {
      scrollIntoViewIfNeeded(card, 'center');
    }
  };

  /* ----- map ----- */

  FindRetailer.prototype.initMap = function () {
    var self = this;
    loadLeaflet().then(
      function (L) {
        if (!self.root.isConnected) return;
        try {
          self.createMap(L);
        } catch (e) {
          self.mapFailed();
        }
      },
      function () {
        self.mapFailed();
      }
    );
  };

  FindRetailer.prototype.mapFailed = function () {
    this.map = null;
    this.root.classList.add('aps-fr--no-map');
    this.els.mapFrame.classList.add('is-failed');
    this.els.mapMsg.textContent = this.t.mapFailed;
    this.els.mapMsg.hidden = false;
  };

  FindRetailer.prototype.createMap = function (L) {
    var self = this;
    var cfg = this.config;
    var touchOnly = !!(window.matchMedia && window.matchMedia(TOUCH_ONLY).matches);
    var map = L.map(this.els.map, {
      scrollWheelZoom: false, // the page scrolls normally; use + and - (or two fingers) to zoom
      // On a phone one finger scrolls the page (not the map); two fingers move and zoom the map.
      dragging: !touchOnly,
      touchZoom: true,
      worldCopyJump: true,
      minZoom: 1,
      zoomAnimation: !reduceMotion,
      fadeAnimation: !reduceMotion,
      markerZoomAnimation: !reduceMotion
    });
    this.L = L;
    this.map = map;
    map.attributionControl.setPrefix(LEAFLET_PREFIX);

    var tiles = L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION });
    var loaded = 0;
    var failed = 0;
    tiles.on('tileload', function () {
      loaded++;
      if (self.tilesNoticeShown) {
        self.els.mapMsg.hidden = true;
        self.tilesNoticeShown = false;
      }
    });
    tiles.on('tileerror', function () {
      failed++;
      if (!loaded && failed >= 3 && !self.tilesNoticeShown) {
        self.tilesNoticeShown = true;
        self.els.mapMsg.textContent = self.t.tilesFailed;
        self.els.mapMsg.hidden = false;
        self.els.mapFrame.classList.add('has-notice');
      }
    });
    tiles.addTo(map);

    this.layer = L.layerGroup().addTo(map);
    this.popup = L.popup({
      maxWidth: 280,
      offset: L.point(0, -48),
      autoPanPaddingTopLeft: L.point(64, 16),
      autoPanPaddingBottomRight: L.point(16, 16)
    });
    map.on('popupclose', function (e) {
      if (e.popup === self.popup && !self.ignorePopupClose) {
        self.state.selected = null;
        self.refreshSelection();
      }
    });
    // Keep every pin on the copy of the world that is in view (near the date line).
    map.on('moveend', function () {
      self.wrapMarkers();
    });

    this.retailers.forEach(function (r) {
      var marker = L.marker([r.pinLat, r.pinLng], {
        keyboard: false,
        title: r.name,
        riseOnHover: true,
        icon: self.pinIcon('', false)
      });
      marker.on('click', function () {
        self.select(r.id);
        self.openPopup(r);
        self.revealCard(r.id, false);
      });
      self.markers[r.id] = marker;
      self.markerKeys[r.id] = '|false';
    });

    var lat = toNumber(cfg.mapLat);
    var lng = toNumber(cfg.mapLng);
    var zoom = parseInt(cfg.mapZoom, 10) || 4;
    if (window.matchMedia(NARROW).matches) zoom = Math.max(1, zoom - 1);
    if (isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      map.setView([lat, lng], zoom);
    } else {
      this.fitPoints(
        this.retailers.map(function (r) {
          return [r.lat, r.lng];
        })
      );
    }

    this.els.mapMsg.hidden = true;
    this.els.mapFrame.classList.add('is-ready');
    this.renderMarkers();
    this.updateOriginMarker();
    if (this.state.mode !== 'none') this.fitToResults();
    if (this.state.selected && this.byId[this.state.selected]) this.openPopup(this.byId[this.state.selected]);
  };

  FindRetailer.prototype.pinIcon = function (label, selected) {
    var L = this.L || window.L;
    // The pin is a square turned 45 degrees with three round corners; its point sits
    // half a side plus half a diagonal below the top (36px for a 30px pin).
    var side = selected ? 38 : 30;
    var tip = Math.round(side / 2 + (side * Math.SQRT2) / 2);
    return L.divIcon({
      className: 'aps-fr-pin' + (selected ? ' is-selected' : '') + (label ? ' has-label' : ''),
      html:
        '<span class="aps-fr-pin__body"><span class="aps-fr-pin__label">' +
        (label ? String(parseInt(label, 10)) : '') +
        '</span></span>',
      iconSize: [side, tip + 1],
      iconAnchor: [side / 2, tip]
    });
  };

  FindRetailer.prototype.renderMarkers = function () {
    if (!this.map) return;
    var self = this;
    var visible = {};
    this.filtered().forEach(function (r) {
      visible[r.id] = true;
    });
    var numbers = {};
    var s = this.state;
    s.list.slice(0, Math.min(s.shown, s.list.length)).forEach(function (r, i) {
      numbers[r.id] = i + 1;
    });
    this.retailers.forEach(function (r) {
      var marker = self.markers[r.id];
      if (!marker) return;
      if (!visible[r.id]) {
        if (self.layer.hasLayer(marker)) self.layer.removeLayer(marker);
        return;
      }
      var selected = s.selected === r.id;
      var key = (numbers[r.id] || '') + '|' + selected;
      if (self.markerKeys[r.id] !== key) {
        marker.setIcon(self.pinIcon(numbers[r.id] || '', selected));
        // Leaflet stacks pins by how far down the map they are; these offsets win over that,
        // so the selected pin is on top, then 1, 2, 3...
        marker.setZIndexOffset(selected ? 200000 : numbers[r.id] ? 100000 - numbers[r.id] * 100 : 0);
        self.markerKeys[r.id] = key;
      }
      if (!self.layer.hasLayer(marker)) self.layer.addLayer(marker);
    });
  };

  FindRetailer.prototype.wrapMarkers = function () {
    if (!this.map) return;
    var ref = this.map.getCenter().lng;
    var self = this;
    var move = function (layer, lat, lng) {
      var want = unwrapLng(lng, ref);
      if (Math.abs(layer.getLatLng().lng - want) > 1e-9) layer.setLatLng([lat, want]);
    };
    this.retailers.forEach(function (r) {
      if (self.markers[r.id]) move(self.markers[r.id], r.pinLat, r.pinLng);
    });
    var o = this.state.origin;
    if (this.originMarker && o) move(this.originMarker, o.lat, o.lng);
  };

  FindRetailer.prototype.updateOriginMarker = function () {
    if (!this.map) return;
    var L = this.L;
    var o = this.state.origin;
    if (this.originMarker) {
      this.map.removeLayer(this.originMarker);
      this.originMarker = null;
    }
    if (!o) return;
    this.originMarker = L.circleMarker([o.lat, o.lng], {
      radius: 9,
      weight: 3,
      color: '#ffffff',
      fillColor: '#1d4ed8',
      fillOpacity: 1,
      className: 'aps-fr-origin'
    })
      .bindTooltip(o.kind === 'you' ? this.t.youAreHere : this.t.yourSearch)
      .addTo(this.map);
  };

  FindRetailer.prototype.fitPoints = function (points) {
    if (!this.map || !points.length) return;
    if (points.length === 1) {
      this.map.setView(points[0], 12, { animate: !reduceMotion });
      return;
    }
    this.map.fitBounds(this.L.latLngBounds(points), {
      padding: [40, 40],
      maxZoom: 12,
      animate: !reduceMotion
    });
  };

  // Fits the search point and the nearest shops: those within 3 times the nearest one's
  // distance (and at least FIT_MIN_KM), so far-away shops in the list don't shrink the map to the
  // whole world. Longitudes are taken around the search point (the date line).
  FindRetailer.prototype.fitToResults = function () {
    if (!this.map) return;
    var s = this.state;
    var shops = s.list.slice(0, Math.min(s.shown, s.list.length));
    if (s.origin && shops.length && isFinite(shops[0].km)) {
      var reach = Math.max(shops[0].km * 3, FIT_MIN_KM);
      shops = shops.filter(function (r, i) {
        return i === 0 || r.km <= reach;
      });
    }
    var ref = s.origin ? s.origin.lng : shops.length ? shops[0].lng : 0;
    var points = shops.map(function (r) {
      return [r.lat, unwrapLng(r.lng, ref)];
    });
    if (s.origin) points.unshift([s.origin.lat, s.origin.lng]);
    this.fitPoints(points);
  };

  FindRetailer.prototype.popupContent = function (r) {
    var self = this;
    var box = el('div', 'aps-fr-pop');
    box.appendChild(el('strong', 'aps-fr-pop__name', r.name));
    if (r.sells.length) box.appendChild(el('span', 'aps-fr-pop__sells', this.sellsNames(r).join(' · ')));
    if (r.noFront) {
      var based = this.basedInText(r);
      if (based) box.appendChild(el('span', 'aps-fr-pop__line', based));
    } else {
      var place = [r.city, r.regionFull || r.region].filter(Boolean).join(', ');
      if (place) box.appendChild(el('span', 'aps-fr-pop__line', place));
    }
    if (r.hours) box.appendChild(this.hoursNode(r, 'span', 'aps-fr-pop__line'));
    if (r.km !== null && this.state.origin) box.appendChild(el('span', 'aps-fr-pop__line', this.distanceText(r.km)));
    var more = el('button', 'aps-fr-pop__more', this.t.seeDetails);
    more.type = 'button';
    more.appendChild(hiddenText(' – ' + r.name));
    more.addEventListener('click', function () {
      self.revealCard(r.id, true);
    });
    box.appendChild(more);
    return box;
  };

  FindRetailer.prototype.openPopup = function (r) {
    if (!this.map) return;
    var marker = this.markers[r.id];
    this.popup.setLatLng(marker ? marker.getLatLng() : [r.pinLat, r.pinLng]).setContent(this.popupContent(r));
    if (!this.map.hasLayer(this.popup)) this.popup.openOn(this.map);
  };

  FindRetailer.prototype.destroy = function () {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  };

  /* ---------- start ---------- */

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-aps-find-retailer]');
    Array.prototype.forEach.call(roots, function (root) {
      if (!root.__apsFindRetailer) root.__apsFindRetailer = new FindRetailer(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: redraw when the section is changed, tidy up when it's removed.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll('[data-aps-find-retailer]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__apsFindRetailer) root.__apsFindRetailer.destroy();
    });
  });
})();
